import { Box } from '@mui/system';
import { useEffect, useState } from 'react'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Button, CircularProgress } from '@mui/material';
import { BsFileEarmarkPdfFill } from 'react-icons/bs'
import { getScanPdf } from '../../utils/api';
import RiceBasedkernalLength from './RiceBasedkernalLength';
import CircularLoadingPopUp from '../../components/CircularLoadingPopUp';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';

const ACCENT_BLUE = '#22638f';
const ACCENT_ORANGE = '#d96c3c';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

const cardShell = {
  borderRadius: '16px',
  boxShadow: '0 2px 10px rgba(12, 61, 110, 0.05)',
  border: `1px solid ${BORDER}`,
};

// Small pill badge used for header-line items (Crop, Scan ID, Total Sub Scan, Quality Score)
function InfoBadge({ label, value, accent }) {
  if (value === undefined || value === null || value === '') return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        border: `1px solid ${BORDER}`,
        borderRadius: '999px',
        px: 1.75,
        py: 0.6,
        backgroundColor: accent ? `${ACCENT_BLUE}0D` : '#fafcff',
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TEXT_MUTED,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: accent ? ACCENT_BLUE : TEXT_BODY,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </Box>
  );
}

function ResultScreen({ scanId, totalSubScan, cropList, cropId, scanResult, moisture, weight, visiblePDfButton, isChecked, saveTestVariety }) {
  const [cropName, setCropName] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false)

  useEffect(() => {
    setCropName(cropId && cropList.find(crop => crop.id === cropId))
  }, [cropId])

  const downloadScanPDf = async () => {
    setLoadingPdf(true)
    try {
      await getScanPdf(scanId)
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingPdf(false)
    }
  }

  return (
    <Box sx={{ ...cardShell, backgroundColor: '#fff', p: 3, mt: 3 }}>
      {/* Header — title, badges, and actions all in one line */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <Box
          sx={{
            width: 34, height: 34, borderRadius: '10px',
            backgroundColor: `${ACCENT_ORANGE}1A`, color: ACCENT_ORANGE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <AssessmentRoundedIcon sx={{ fontSize: 19 }} />
        </Box>
        <p style={{ fontSize: 16, fontWeight: 700, color: TEXT_BODY, margin: 0 }}>
          Quality Assessment Report
        </p>

        <InfoBadge label="Crop" value={cropName === null ? ' ' : cropName?.crop} />
        <InfoBadge label="Scan ID" value={scanId} />
        <InfoBadge label="Total Sub Scan" value={totalSubScan} />
        <InfoBadge label="Quality Score" value={scanResult?.kernel_details?.overall_score !== undefined ? `${scanResult?.kernel_details?.overall_score}%` : undefined} accent />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', ml: 'auto' }}>
          <Box sx={{ position: 'relative' }}>
            <Button
              variant="contained"
              disabled={!visiblePDfButton}
              startIcon={
                !visiblePDfButton
                  ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                  : <BsFileEarmarkPdfFill />
              }
              onClick={downloadScanPDf}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                backgroundColor: ACCENT_BLUE,
                px: 2.5,
                '&:hover': { backgroundColor: '#1b4f73', boxShadow: 'none' },
                '&.Mui-disabled': { backgroundColor: '#c9d6e2', color: '#fff' },
              }}
            >
              Download PDF
            </Button>
            {loadingPdf && <CircularLoadingPopUp />}
          </Box>

          {isChecked && (
            <Button
              variant="outlined"
              onClick={saveTestVariety}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: BORDER,
                color: TEXT_BODY,
                px: 2.5,
                '&:hover': { borderColor: ACCENT_BLUE, backgroundColor: '#fafcff' },
              }}
            >
              Save Test
            </Button>
          )}
        </Box>
      </Box>

      {scanResult && (
        <Box sx={{ mt: 3 }}>
          <TableComponent
            scanResult={scanResult}
            kernals={scanResult?.kernel_details}
            moisture={scanResult?.moisture_value}
            weight={scanResult?.weight_value}
          />

          {(scanResult?.kernel_details?.range_distributions?.distributions) && (
            <RiceBasedkernalLength
              riceBasedkernalLength={scanResult?.kernel_details?.range_distributions}
              beautify={scanResult?.kernel_details?.beautify?.translations?.range_distributions}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default ResultScreen


// Result Response Table
export function TableComponent({ scanResult, moisture, weight }) {
  const rows = [];
  let headers = [
    { label: "", align: "left" },
    { label: "Particulars", align: "left" },
  ];

  headers.push({ label: "Count", align: "left" });
  headers.push({ label: "Count %", align: "left" });
  headers.push({ label: "Weight (gm)", align: "left" });
  headers.push({ label: "Weight (%)", align: "left" });

  if (scanResult?.kernel_details?.grading) {
    if (scanResult?.kernel_details?.max_limit) {
      headers.push({ label: "% By", align: "left" });
      headers.push({ label: "Maximum Limit", align: "left" });
    } else {
      headers.push({ label: "Grade", align: "left" });
      headers.push({ label: "% By", align: "left" });
      headers.push({ label: "Range 1", align: "left" });
      headers.push({ label: "Range 2", align: "left" });
      headers.push({ label: "Range 3", align: "left" });
    }
  }

  if (scanResult?.kernel_details?.consider_area) {
    headers.push({ label: "Area (mm2)", align: "left" });
  }

  if (scanResult?.kernel_details?.consider_length) {
    headers.push({ label: "Length (mm)", align: "left" });
  }

  const firstRow = [];
  firstRow.push("");
  firstRow.push(
    scanResult?.kernel_details?.beautify?.translations?.total_kernel_count
  );
  firstRow.push(scanResult?.kernel_details?.total_kernel_count);
  firstRow.push("100%");

  if (parseFloat(Number(weight) ? Number(weight) : scanResult?.kernel_details?.total_kernel_weight) > 0) {
    firstRow.push(parseFloat(weight) > 0 ? parseFloat(weight).toFixed(2) : parseFloat(scanResult?.kernel_details?.total_kernel_weight).toFixed(2))
    firstRow.push("100%");
  }

  if (scanResult?.kernel_details?.grading) {
    if (scanResult?.kernel_details?.max_limit) {
      firstRow.push("NA", "NA");
    } else {
      firstRow.push("NA", "NA", "NA", "NA", "NA");
    }
  }

  if (scanResult?.kernel_details?.consider_area) {
    firstRow.push("");
  }
  if (scanResult?.kernel_details?.consider_length) {
    firstRow.push("");
  }
  rows.push(firstRow);

  let totalCount = 0;

  scanResult?.kernel_details?.classes.forEach((key, index) => {
    let row = [];
    row.push({
      cellType: "box",
      color: scanResult?.processed_image?.encoding_colors[key],
    });
    row.push(
      scanResult?.kernel_details?.beautify?.translations?.kernel_counts[key] +
      " (" +
      scanResult?.processed_image?.encodings[key] +
      ")"
    );

    row.push(Number(scanResult?.kernel_details?.kernel_counts[key]));
    row.push(
      ` ${Number(
        (scanResult?.kernel_details?.kernel_counts[key] /
          scanResult?.kernel_details?.total_kernel_count) *
        100
      ).toFixed(2)}%`
    );

    if (parseFloat(scanResult?.kernel_details?.total_kernel_weight) > 0) {
      let weightKey = scanResult?.kernel_details?.kernel_weights || scanResult?.kernel_details?.kernel_weight;
      const totalWeight = parseFloat(scanResult?.kernel_details?.total_kernel_weight);
      const actualWeight = parseFloat(weight || totalWeight);

      if (actualWeight === 0) {
        row.push(Number(weightKey[key]).toFixed(2));
        row.push(((weightKey[key] / totalWeight) * 100).toFixed(2));
      } else {
        const weightedPercentage = (weightKey[key] / totalWeight) * 100;
        const finalWeight = (weightedPercentage * actualWeight) / 100;
        row.push(finalWeight.toFixed(2));
        row.push(weightedPercentage.toFixed(2));
      }
    } else {
      row.push("NA");
      row.push("NA");
    }

    if (scanResult?.kernel_details?.grading) {
      if (scanResult?.kernel_details?.max_limit) {
        let classGrading = scanResult?.kernel_details?.grading[key]
        let rangeValues = classGrading?.weightRange || classGrading?.countRange || ["NA"];
        if (classGrading?.weightRange) {
          row.push('Weight');
        } else if (classGrading?.countRange) {
          row.push('Count');
        } else {
          row.push('NA');
        }
        row.push(rangeValues[0]);
      } else {
        const value = scanResult?.kernel_details?.class_ranges[key];
        row.push(value > 0 ? value : 'NA');
        let classGrading = scanResult?.kernel_details?.grading[key]
        let rangeValues = classGrading?.weightRange || classGrading?.countRange || ["NA", "NA", "NA"];
        if (classGrading?.weightRange) {
          row.push('Weight');
        } else if (classGrading?.countRange) {
          row.push('Count');
        } else {
          row.push('NA');
        }
        row.push(rangeValues[0]);
        row.push(rangeValues[1]);
        row.push(rangeValues[2]);
      }
    }

    if (scanResult?.kernel_details?.consider_area && scanResult?.kernel_details?.areas) {
      row.push(Number(scanResult?.kernel_details?.areas[key]).toFixed(2));
    }

    if (scanResult?.kernel_details?.consider_length && scanResult?.kernel_details?.kernel_lengths) {
      row.push(
        Number(scanResult?.kernel_details?.kernel_lengths[key]).toFixed(2)
      );
    }
    const uniqueCombinations = new Map();

    const filteredKernel = Array.from(uniqueCombinations.values()).filter(
      (obj) => obj.originalClassName !== obj.changedClassName
    );
    const count = filteredKernel ? filteredKernel.length : 0;
    totalCount += count;

    rows.push(row);
  });

  const hasMoisture = moisture !== undefined && moisture !== null && moisture !== 0 && moisture !== '';

  if (hasMoisture) {
    const lastRow = [];
    lastRow.push("");
    lastRow.push("Moisture");
    lastRow.push("-");
    lastRow.push("-");
    lastRow.push("-");
    lastRow.push(`${moisture} %`);

    if (scanResult?.kernel_details?.grading) {
      let moistureGrading = scanResult?.kernel_details?.grading?.moisture
      if (scanResult?.kernel_details?.max_limit) {
        let rangeValues = moistureGrading?.weightRange || moistureGrading?.countRange || ["NA"];
        lastRow.push("NA");
        lastRow.push(rangeValues[0] || "NA");
      } else {
        let rangeValues = moistureGrading?.weightRange || moistureGrading?.countRange || ["NA", "NA", "NA"];
        lastRow.push(scanResult?.kernel_details?.moisture_range)
        lastRow.push("NA");
        lastRow.push(rangeValues[0]);
        lastRow.push(rangeValues[1]);
        lastRow.push(rangeValues[2]);
      }
    }

    if (scanResult?.kernel_details?.consider_area) {
      lastRow.push("-");
    }
    if (scanResult?.kernel_details?.consider_length) {
      lastRow.push("-");
    }

    rows.push(lastRow);
  }

  const tcwTotalCount = scanResult?.kernel_details?.total_kernel_count;
  const tcwWeightSource = parseFloat(weight) > 0
    ? parseFloat(weight)
    : parseFloat(scanResult?.kernel_details?.total_kernel_weight || 0);
  const tcwValue = tcwTotalCount > 0 && tcwWeightSource > 0
    ? ((tcwWeightSource * 1000) / tcwTotalCount).toFixed(2)
    : "NA";

  const tcwRow = [];
  tcwRow.push("");
  tcwRow.push("Thousand Grain Weight (TGW)");
  tcwRow.push(""); 
  tcwRow.push(""); 
  tcwRow.push(`${tcwValue} gm`); 
  tcwRow.push(""); 

  if (scanResult?.kernel_details?.grading) {
    if (scanResult?.kernel_details?.max_limit) {
      tcwRow.push("NA", "NA");
    } else {
      tcwRow.push("NA", "NA", "NA", "NA", "NA");
    }
  }
  if (scanResult?.kernel_details?.consider_area) {
    tcwRow.push("");
  }
  if (scanResult?.kernel_details?.consider_length) {
    tcwRow.push("");
  }

  rows.push(tcwRow);

  const table = {
    title: "",
    subtitle: "",
    headers: headers,
    rows: rows,
  };

  return (
    <Box>
      <TableContainer
        sx={{
          ...cardShell,
          backgroundColor: '#fff',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {table.headers.map((header, index) => (
                <TableCell
                  key={index}
                  align={header.align}
                  sx={{
                    backgroundColor: '#fafcff',
                    borderBottom: `1px solid ${BORDER}`,
                    fontSize: '11.5px',
                    fontWeight: 700,
                    color: TEXT_MUTED,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 1.5,
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#f8fbfe' : '#ffffff',
                  '&:hover': { backgroundColor: '#eef4fb' },
                }}
              >
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    align="left"
                    sx={{
                      borderBottom: 'none',
                      fontSize: '13px',
                      color: TEXT_BODY,
                      py: 1.2,
                    }}
                  >
                    {cell && cell.cellType === "box" ? (
                      <Box
                        sx={{
                          backgroundColor: cell.color,
                          width: 18,
                          height: 18,
                          borderRadius: '5px',
                          border: `1px solid ${BORDER}`,
                        }}
                      />
                    ) : (
                      <div>{cell}</div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}